import {
    Button,
    FormField,
    Radiobox,
    SpinnerCircular,
    Notification,
    InfoNotification,
    ProcessStepper
} from '@weirgroup/weir-dls'
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { PositionType, SingleAssetGeofencesType } from '../../types/geofence.type'
import { GEOFENCE_SHAPE } from '../../constants/bucket.contants'
import { TagType } from '../../types/bucketTypes'
import GoogleApiWrapper, { GeofenceMapRef } from './components/geofence-map'
import { useTranslation } from 'react-i18next'
import geofenceApi from '../../api/geofence.api';
import './styles.scss'

interface Props {
    setShowAdd: Dispatch<SetStateAction<boolean>>
    tagData: TagType
    selectedGeofence?: SingleAssetGeofencesType | null
    setSelectedGeofence?: Dispatch<SetStateAction<SingleAssetGeofencesType | null>>
    refreshList: () => void;
    setIsAssetUpdated: Dispatch<SetStateAction<boolean>>
    darkMode?: boolean
}

// Reusable Button component
const StepButton = ({
    darkMode,
    label,
    onClick,
    disabled
}: {
    darkMode?: boolean,
    label: string,
    onClick: () => void,
    disabled?: boolean
}) => (
    <div className="btn-container">
        <Button
            darkMode={darkMode}
            label={label}
            size={'sm'}
            variant={'primary'}
            onClick={onClick}
            disabled={disabled}
        />
    </div>
);

// Reusable Geofence Name Field
const GeofenceNameField = ({
    darkMode,
    value,
    onChange,
    defaultValue,
    t,
    selectedGeofence
}: {
    darkMode?: boolean,
    value: string,
    onChange: (e: any) => void,
    defaultValue?: string,
    t: any,
    selectedGeofence?: any
}) => (
    <FormField
        darkMode={darkMode}
        name="InputBox"
        label={t("geofenceForm.geofenceName")}
        disabled={false}
        size={'sm'}
        width={'100%'}
        placeholder={t("geofenceForm.geofenceName")}
        onChange={onChange}
        required
        defaultValue={selectedGeofence?.name ?? value}
    />
);

export const AddGeofence = (props: Props) => {
    const {
        setShowAdd,
        tagData,
        selectedGeofence,
        setSelectedGeofence,
        refreshList,
        setIsAssetUpdated,
        darkMode
    } = props
    const { t } = useTranslation();
    const mapRef = useRef<GeofenceMapRef>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [geofenceName, setGeofenceName] = useState<string>('')
    const [geofenceRadius, setGeofenceRadius] = useState<number>(50)
    const [geofenceCoordinates, setGeofenceCoordinates] = useState<
        PositionType[]
    >([])
    const [messageState, setMessageState] = useState<any>({title:"Message Title",textContext:"Message State", variant:"success", open:false})
    const [showToast, setShowToast] = useState<boolean>(false)
    const [toastData, setToastData] = useState<any>()
    const [step, setStep] = useState<any>([
        {
            state:"active",
            title: t("geofenceForm.name"),
        },
        {
            state:"active",
            title: t("geofenceForm.type"),
        },
        {
            state:"active",
            title: t("geofenceForm.location"),
        },
        {
            state:"active",
            title: t("geofenceForm.radius"),
        },

    ])
    useEffect(() => {
        if (showToast) {
            setTimeout(() => setShowToast(false), 3000)
        }
    }, [showToast])

    const showErrorMessage = (message: string) => {
        setToastData({
            variant: 'critical',
            message,
        })
        setShowToast(true)
    }
    const [selectedShape, setSelectedShape] = useState({
        circle: true,
        polygon: false,
    })

    let geofenceType: GEOFENCE_SHAPE = selectedShape.polygon
        ? GEOFENCE_SHAPE.POLYGON
        : GEOFENCE_SHAPE.CIRCLE

    const assetCoordinates = useMemo(() => {
        if (tagData) {
            const asset = tagData;
            if (asset.location?.latitude && asset.location?.longitude) {
                const { latitude: lat, longitude: lng } =
                    asset.location
                const location = new google.maps.LatLng({ lat, lng })
                return location
            } else return undefined
        }
    }, [tagData])

    const [selectedLocation, setSelectedLocation] = useState({
        assetLocation: false,
        newLocation: false,
    })

    const [showTab, setShowTab] = useState({
        name: true,
        type: false,
        location: false,
        radius: false,
    })

    const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false)

    useEffect(() => {
        const checker = !geofenceCoordinates || !geofenceName || (selectedShape.circle && !geofenceRadius) || !geofenceType;
        setIsSubmitDisabled(checker);
    }, [selectedLocation, geofenceCoordinates, geofenceName, selectedShape, geofenceRadius, geofenceType])


    const changeShape = useCallback(
        (shape: GEOFENCE_SHAPE) => () => {
            setSelectedShape({
                circle: shape == GEOFENCE_SHAPE.CIRCLE,
                polygon: shape == GEOFENCE_SHAPE.POLYGON,
            })
            if (shape == GEOFENCE_SHAPE.POLYGON) {
                setSelectedLocation({
                    assetLocation: false,
                    newLocation: true,
                })
            } else {
                setSelectedLocation({
                    assetLocation: false,
                    newLocation: false,
                })
            }
            setGeofenceCoordinates([])
        },
        [selectedShape]
    )

    useEffect(() => {
        if (selectedLocation.assetLocation) {
            const coords = [{
                order: 0,
                latitude: tagData.location.latitude,
                longitude: tagData.location.longitude
            }]
            setGeofenceCoordinates(coords)
        }
    }, [selectedLocation])

    useEffect(() => {
        if (selectedShape.polygon) {
            setSelectedLocation({
                assetLocation: false,
                newLocation: true,
            })
        }
    }, [selectedShape])

    useEffect(() => {
        if (selectedGeofence) {
            setGeofenceName(selectedGeofence.name)
            setGeofenceRadius(selectedGeofence.radius)
            geofenceType = selectedGeofence.shape == "circle" ? GEOFENCE_SHAPE.POLYGON : GEOFENCE_SHAPE.CIRCLE
            setGeofenceCoordinates(JSON.parse(selectedGeofence.coordinates))
            setSelectedShape({
                circle: selectedGeofence.shape == "circle",
                polygon: selectedGeofence.shape == "polygon"
            })
        }
    }, [selectedGeofence])

    useEffect(() => {
        if (showToast) {
            setTimeout(() => setShowToast(false), 5000)
        }
    }, [toastData])

    const onLocationNext = (index:any) => {
        if (selectedShape.polygon) {
            submitGeofence(index);
        } else {
            setShowTab({
                name: false,
                type:false,
                location: false,
                radius: true,
            })
            let stepContainer = [...step]
            stepContainer[index].state = "success"
            setStep(stepContainer)
        }
    }

    // Refactored nameTab
    const nameTab = (index: any) =>{
        return(
            <div className="name-type">
                <div style={{marginTop:10}}></div>
                <GeofenceNameField
                    darkMode={darkMode}
                    value={geofenceName}
                    onChange={(e: any) => setGeofenceName(e.target.value)}
                    defaultValue={selectedGeofence?.name}
                    t={t}
                    selectedGeofence={selectedGeofence}
                />
                <div style={{marginTop:20, marginBottom:20}}></div>
                <StepButton
                    darkMode={darkMode}
                    label={t("geofenceForm.next")}
                    onClick={() => {
                        if (geofenceName.trim().length != 0) {
                            let stepContainer = [...step]
                            stepContainer[index].state = "success"
                            setStep(stepContainer)
                            setShowTab({
                                name: false,
                                type:true,
                                location: false,
                                radius: false,
                            })
                        } else {
                            showErrorMessage(t("validGeofenceNameMessage"))
                        }
                    }}
                    disabled={
                        geofenceName.length == 0 ||
                        (!selectedShape.circle && !selectedShape.polygon)
                    }
                />
            </div>) 
    }

    // Refactored typeTab
    const typeTab = (index:any) => {
        return(
            <div className="name-type">
                <div className="shape">
                    <p className="title">{t("geofenceForm.selectShape")}</p>
                    <div className="radio-btn">
                        <Radiobox
                            darkMode={darkMode}
                            name="circle"
                            label={t("geofenceForm.circle")}
                            value={'circle'}
                            checked={selectedShape.circle}
                            disabled={selectedGeofence}
                            onChange={changeShape(GEOFENCE_SHAPE.CIRCLE)}
                        />
                        <Radiobox
                            darkMode={darkMode}
                            name="polygon"
                            label={t("geofenceForm.polygon")}
                            value={'polygon'}
                            checked={selectedShape.polygon}
                            disabled={selectedGeofence}
                            onChange={changeShape(GEOFENCE_SHAPE.POLYGON)}
                        />
                    </div>
                    <div style={{marginTop:10, marginBottom:10}}></div>
                    <StepButton
                        darkMode={darkMode}
                        label={t("geofenceForm.next")}
                        onClick={() => {
                            if (geofenceName.trim().length != 0) {
                                let stepContainer = [...step]
                                stepContainer[index].state = "success"
                                setStep(stepContainer)
                                setShowTab({
                                    name: false,
                                    type:false,
                                    location: true,
                                    radius: false,
                                })
                            } else {
                                showErrorMessage(t("validGeofenceNameMessage"))
                            }
                        }}
                        disabled={
                            geofenceName.length == 0 ||
                            (!selectedShape.circle && !selectedShape.polygon)
                        }
                    />
                </div>
            </div>
        )
    }

    const renderSpinner = () => {
        return (
            <div className="spinner">
                <SpinnerCircular darkMode={darkMode}/>
                <p>{t("loading")}</p>
            </div>
        )
    }

    const locationTab = (index: any) => {
        return (
            <div className="location-container">
                {selectedLocation.newLocation || (selectedGeofence?.coordinates) ? (
                    <div className="map-container">
                        <GoogleApiWrapper
                            ref={mapRef}
                            radius={geofenceRadius}
                            geofenceType={geofenceType}
                            assetCoordinates={assetCoordinates}
                            geofenceCoordinates={geofenceCoordinates}
                            setGeofenceCoordinates={setGeofenceCoordinates}
                            selectedGeofence={selectedGeofence}
                        />
                    </div>
                ) : (
                    <></>
                )}
                {(selectedShape.circle && !selectedGeofence) ? (
                    <div className="radio-btn">
                        <Radiobox
                            darkMode={darkMode}
                            name="Asset Location"
                            label={t("geofenceForm.assetLocation")}
                            value={'Asset Location'}
                            checked={selectedLocation.assetLocation}
                            disabled={assetCoordinates == undefined}
                            onChange={() => {
                                setSelectedLocation({
                                    assetLocation: true,
                                    newLocation: false,
                                })
                            }}

                        />
                        <Radiobox
                            darkMode={darkMode}
                            name="New Location"
                            label={t("geofenceForm.newLocation")}
                            value={'New Location'}
                            checked={selectedLocation.newLocation}
                            disabled={false}
                            onChange={() => {
                                setSelectedLocation({
                                    assetLocation: false,
                                    newLocation: true,
                                })
                            }}
                        />

                    </div>
                ) : (
                    <></>
                )}
                {isLoading && selectedShape.polygon ? renderSpinner() : <></>}
                <div className='size-box'></div>
                <div className="btn-container">
                    {selectedShape.polygon && (
                        <Button
                            darkMode={darkMode}
                            label={t("geofenceForm.deleteLastPoint")}
                            size={'sm'}
                            variant={'primary'}
                            onClick={() => mapRef.current?.removeLastPoint()}
                        />
                    )}
                    {selectedShape.polygon ?
                        <Button
                            darkMode={darkMode}
                            label={t("geofenceForm.submit")}
                            disabled={isSubmitDisabled || geofenceCoordinates.length < 3}
                            size={'sm'}
                            variant={'primary'}
                            onClick={() => submitGeofence(index)}
                        />
                        :
                        <Button
                            darkMode={darkMode}
                            label={t("geofenceForm.next")}
                            size={'sm'}
                            variant={'primary'}
                            onClick={()=>onLocationNext(index)}
                            disabled={
                                selectedGeofence ? isSubmitDisabled : (!selectedLocation.assetLocation && !selectedLocation.newLocation)
                            }
                        />
                    }
                </div>
            </div>
        )
    }

    const radiusTab = (index:any) => {
        return (
            <div className="location-container">
                {isLoading ? renderSpinner() : <></>}
                <div style={{marginTop:10, marginBottom:10}}></div>
                <FormField
                    darkMode={darkMode}
                    name="InputBox"
                    required={true}
                    label={t("geofenceForm.selectRadius")}
                    type="number"
                    min="1"
                    size="sm"
                    width="100%"
                    placeholder={t("geofenceForm.makeSelection")}
                    onChange={(e: any) => {
                        if (e.target.value >= 0) {
                            setGeofenceRadius(e.target.value);
                        }
                    }}
                    onKeyPress={(e: any) => {
                        // Prevent invalid characters like 'e', 'E', '+', '-', and '.'
                        if (["e", "E", "+", "-", "."].includes(e.key)) {
                            e.preventDefault();
                        }
                    }}
                    defaultValue={selectedGeofence?.radius ?? geofenceRadius}
                />
                <div className="btn-container submit-btn-circle">
                    <Button
                        darkMode={darkMode}
                        label={t("geofenceForm.submit")}
                        disabled={isSubmitDisabled}
                        size={'sm'}
                        variant={'primary'}
                        onClick={() => submitGeofence(index)}
                    />
                </div>
            </div>
        )
    }

    const isCompleteData = (data: any) => {
        const checker = data.name && data.shape && data.coordinates !== "[]"
        if (selectedShape.circle) {
            return checker && data.radius;
        } else {
            return checker
        }
    }

    const submitGeofence = (index:any) => {
        setIsLoading(true);
        const data = {
            asset_id: tagData.asset_id,
            coordinates: JSON.stringify(geofenceCoordinates),
            name: geofenceName,
            radius: geofenceRadius,
            shape: geofenceType,
            geofence_id: selectedGeofence?.geofence_id

        }
        if(geofenceRadius < 0 || geofenceRadius > 4000000){
            setMessageState({title:"Error on adding geofence",textContext:"The radius must be between 0 and 4,000,000.", variant:"alert", open:true})
            setIsLoading(false)
        }
        else if (isCompleteData(data)) {
            const apiData = selectedGeofence ? { data, editGeofence: true } : { data }
            geofenceApi.saveGeofence(apiData).then(() => {
                refreshList();
                setIsLoading(false)
                setShowAdd(false);
                setIsAssetUpdated(true)
                if (setSelectedGeofence) {
                    setSelectedGeofence(null);
                }
                let stepContainer = [...step]
                stepContainer[index].state = "success"
                setStep(stepContainer)
            }).catch((err: any) => {
                setMessageState({title:"Error on adding geofence",textContext:err.response.data.message, variant:"alert", open:true})
                setIsLoading(false)
            })
        } else {
            setToastData({
                message: t("submitGeofenceErrorMessage"),
                variant: "critical"
            })
            setShowToast(true)
            setIsLoading(false)
        }
    }
    
    return (
        <div className="geofence-container">
            <div className="milestone-nav">
               <ProcessStepper 
                modalType="small"
                steps={step}
               />
            </div>
            {showTab.name ? nameTab(0) : <></>}
            {showTab.type ? typeTab(1) : <></>}
            {showTab.location ? locationTab(2) : <></>}
            {showTab.radius ? radiusTab(3) : <></>}
            {messageState?.open && (
                <div className="error-container">
                    <InfoNotification
                            darkMode={darkMode}
                            title={messageState?.title}
                            textContent={messageState?.textContext}
                            variant={messageState?.variant}
                            onClose={() => setMessageState({ ...messageState, open: false })}
                    />
                </div>
            )}
            {showToast ? <div className="toast-container">
                <Notification
                    darkMode={darkMode}
                    title={toastData.variant == "success" ? "Success" : "Fail"}
                    textContent={toastData.message}
                    variant={toastData.variant}
                    onClose={()=>{setShowToast(false)}}
                />
            </div> : <></>}
        </div>
    )
}